import { Box, Stack, Typography, useTheme } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateProjectCache, useGeneratePositionsForProject } from 'src/api/project'
import { invalidateProjectPositionCache } from 'src/api/projectPosition'
import { AppForm, FormTextField } from 'src/components/FormCore'
import { MainButton } from 'src/components/MainButton'
import { DialogSpinner } from 'src/components/Spinner'
import { IMooringType, IPositionWithDrawing } from 'src/generated-types'
import { useZodForm } from 'src/hooks/zod-form'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { z } from 'zod'

interface PositionCountSectionProps {
  positionCounts: {
    noOfFrameLines: number
    noOfCagePositions: number
    noOfBridles: number
    noOfCorners: number
    noOfMooringLines: number
  }
  projectType: number
  noOfRows: number
  noOfCages: number
  serviceRow?: boolean
  customerId: EntityId
  localityId: EntityId
  projectId: EntityId
  handleConfirm: (updatedPositions: IPositionWithDrawing[]) => void
}

const PositionCountSection: React.FC<PositionCountSectionProps> = ({
  positionCounts,
  projectType,
  noOfRows,
  noOfCages,
  serviceRow = false,
  customerId,
  localityId,
  projectId,
  handleConfirm,
}) => {
  const { t: translate } = useTranslation(['position', 'mooring', 'file-upload'])
  const theme = useTheme()

  const { mutateAsync: generatePositions, isLoading } = useGeneratePositionsForProject(
    customerId,
    localityId,
    projectId,
  )

  const positionCountsSchema = z.object({
    noOfCagePositions: z
      .string()
      .or(z.number())
      .transform(val => Number(val)),
    noOfMooringLines: z
      .string()
      .or(z.number())
      .transform(val => Number(val)),
    noOfRows: z
      .string()
      .or(z.number())
      .transform(val => Number(val)),
    type: z.nativeEnum(IMooringType, {}),
  })

  const isFrameCage = projectType === IMooringType.FrameCage

  const positionCountForm = useZodForm(positionCountsSchema, {
    defaultValues: positionCounts,
  })

  const mooringLineCount = positionCountForm.watch('noOfMooringLines')
  const cagePositionsCount = positionCountForm.watch('noOfCagePositions')

  const positionUpdateDisable = useMemo(
    () =>
      positionCounts.noOfCagePositions === Number(cagePositionsCount) &&
      positionCounts.noOfMooringLines === Number(mooringLineCount),
    [mooringLineCount, cagePositionsCount, positionCounts],
  )

  const onPositionUpdate = positionForm => {
    const { noOfCagePositions, ...rest } = positionForm
    const payload = isFrameCage
      ? { ...rest, noOfPens: noOfCagePositions, noOfCages, serviceRow }
      : { ...rest, noOfCages: noOfCagePositions, serviceRow: false }
    generatePositions(payload, {
      onSuccess: data => {
        handleConfirm(data.data)
        invalidateProjectPositionCache.getProjectPositions(customerId, projectId)
        invalidateProjectCache.getProject(customerId, localityId, projectId)
        invalidateProjectCache.getProjectMapSVG(customerId, localityId, projectId)
        toast.success(translate('positions-updated-successfully', { ns: 'position' }))
      },
      onError: () => {
        toast.error(translate('error-updating-positions', { ns: 'position' }))
      },
    })
  }

  const onGeneratePositions = async () => {
    positionCountForm.setValue('type', projectType)
    positionCountForm.setValue('noOfRows', noOfRows)
    positionCountForm.handleSubmit(onPositionUpdate)()
  }

  useEffect(() => {
    positionCountForm.reset({
      type: projectType,
      ...positionCounts,
      noOfRows: noOfRows,
    })
  }, [positionCounts, positionCountForm, projectType, noOfRows])
  return (
    <>
      <Box padding={1}>
        <Typography variant="body1">{translate('setup-facility-text', { ns: 'projects' })}</Typography>
        <AppForm form={positionCountForm}>
          <Stack
            flexDirection={'row'}
            flexWrap={'wrap'}
            justifyContent={'start'}
            alignItems={'center'}
            gap={1}
            paddingY={1}
          >
            {Object.keys(positionCounts).map(key => (
              <ComponentCountItem
                key={key}
                label={translate(key === 'noOfCagePositions' ? 'noOfCages' : key)}
                count={positionCounts[key as keyof typeof positionCounts]}
                name={key}
                max={key === 'noOfCagePositions' ? noOfCages : undefined}
                projectType={projectType}
              />
            ))}
          </Stack>
        </AppForm>
        <Box display={'flex'} justifyContent={'end'}>
          <MainButton
            sx={{ backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain) }}
            disabled={positionUpdateDisable}
            onClick={onGeneratePositions}
          >
            {translate('update', { ns: 'common' })}
          </MainButton>
        </Box>
      </Box>
      {isLoading && <DialogSpinner />}
    </>
  )
}

export default PositionCountSection

export const ComponentCountItem: React.FC<{
  label: string
  count: number
  name: string
  max?: number
  projectType?: number
}> = ({ label, name, max, projectType }) => {
  const theme = useTheme()
  const isDisabled = name === 'noOfMooringLines' || name === 'noOfCagePositions' ? false : true

  return (
    <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1} width={'195px'}>
      <Box className="rounded-md my-1" display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <FormTextField
          name={name}
          type="number"
          sx={{ borderRadius: '4px', width: '50px', padding: 0 }}
          InputProps={{
            sx: {
              borderColor: getThemeColor(theme, MUIThemeColors.primaryMain),
              borderWidth: '1px',
              padding: '0px',
              color: getThemeColor(theme, MUIThemeColors.primaryMain),
              fontWeight: 900,
              '& .MuiInputBase-input': {
                padding: '12px 8px',
              },
            },
            inputProps: { min: 0, max: max },
          }}
          disabled={projectType === IMooringType.Barge && name === 'noOfCagePositions' ? true : isDisabled}
        />
      </Box>
      <Typography variant="body1">{label}</Typography>
    </Box>
  )
}
